import React from 'react';
import PropTypes from 'prop-types';

import Loading from '../../../../components/loading';

import Button from '../../../fragments/button';
import {
  FormInline,
  FileInput
} from '../../../fragments/forms';

import {
  parseInputSequenceFile,
} from '../../../../utils/sequence-helper';

import GenbankButton from '../genbank/genbank-button';

import {
  getGenbankIds,
  batchFetchGenbankSequences,
} from '../genbank/genbank-utils';

import alignAllSequences from '../alignment/align-api';
import loadFromLocalDB from '../hivdb/load-db';
import {removeSequenceWithoutGene} from '../alignment';
import ChooseSeqtypeDialog from './choose-seqtype';

import style from './style.css';


const NOREFSUBST = [{
  id: -0x7f,
  refName: '$$DataUpdateOnly'
}];

export default class UploadPanel extends React.Component {

  static propTypes = {
    rawDataCollection: PropTypes.object.isRequired,
    onUpdate: PropTypes.func.isRequired,
  };

  constructor() {
    super(...arguments);
    this.state = {
      loadingStatus: {
        loading: false,
        loadingText: 'Loading...',
        loadingWarning: null,
      },
      noGBIdx: 1,
      fileName: '',
      showChooseSeqType: false,
    };
  }

  updateLoadingStatus(updateInfo) {
    const {loadingStatus} = this.state;

    if ('loading' in updateInfo) {
      loadingStatus.loading = updateInfo.loading;
    }
    if ('loadingText' in updateInfo) {
      loadingStatus.loadingText = updateInfo.loadingText;
    }
    if ('loadingWarning' in updateInfo) {
      loadingStatus.loadingWarning = updateInfo.loadingWarning;
    }
    this.setState({
      loadingStatus,
    });
  }

  async loadGenbankSequences(
    genbankIdsWithRefs,
    rawDataCollection,
    giKey = true,
    replaceSeq = true) {

    let rawSequences = [];
    if (!replaceSeq) {
      rawSequences = rawDataCollection.rawSequences;
    }

    let totalNum = genbankIdsWithRefs.reduce(
      (totalNum, [,,g]) => totalNum + g.length,
      rawSequences.length
    );

    const rawSeqs = batchFetchGenbankSequences(
      genbankIdsWithRefs, giKey);

    let loadingText;
    let timeLastBatch = new Date().getTime();
    // update every 1.5 seconds
    const timeGap = 1500;

    for await (let rawSeq of rawSeqs) {
      rawSequences.push(rawSeq);

      const now = new Date().getTime();

      if (now - timeLastBatch > timeGap) {
        loadingText = `Loading Sequence: ${rawSequences.length}/${totalNum}`;
        this.updateLoadingStatus({
          loadingText: loadingText,
        });

        rawDataCollection.rawSequences = rawSequences;

        timeLastBatch = now;
      }
    }

    rawDataCollection.rawSequences = rawSequences;

    this.updateLoadingStatus({
      loadingText: loadingText,
    });

    return rawDataCollection;
  }

  async alignSequences(rawDataCollection) {
    if (rawDataCollection.sequenceType !== 'complete') {
      return rawDataCollection;
    }

    rawDataCollection = await alignAllSequences(
      rawDataCollection,
      this.updateLoadingStatus.bind(this),
    );

    rawDataCollection = removeSequenceWithoutGene(
      rawDataCollection
    );
    return rawDataCollection;
  }

  async handleLoadFromPMID(e) {
    e && e.preventDefault();

    this.updateLoadingStatus({
      loading: true,
      loadingWarning: null,
    });

    let {rawDataCollection} = this.props;
    rawDataCollection.sequenceType = 'complete';
    rawDataCollection.loadFrom = 'Genbank';

    const {references} = rawDataCollection;

    let medlineIds = [];
    if (references) {
      medlineIds = references.map(({medlineId, id, refName}) =>
        [medlineId, id, refName]
      );
    }

    const genbankIdsWithRefs = [];
    for (const [medlineId, id, refName] of medlineIds) {
      const gids = await getGenbankIds(medlineId);
      genbankIdsWithRefs.push([id, refName, gids]);
    }
    rawDataCollection = await this.loadGenbankSequences(
      genbankIdsWithRefs, rawDataCollection);

    const {rawSequences} = rawDataCollection;

    if (rawSequences.length === 0) {
      const warning = (
        'No sequence can be loaded using PMID, please use genbank numbers or load fasta file.'
      );
      this.updateLoadingStatus({
        loading: false,
        loadingWarning: warning,
      });
      return;
    }

    rawDataCollection = await this.alignSequences(rawDataCollection);
    rawDataCollection = await loadFromLocalDB(rawDataCollection);

    this.updateLoadingStatus({
      loading: false,
    });
    const {onUpdate} = this.props;
    await onUpdate(rawDataCollection);
  }

  async handleLoadFromAccession(genbankIds, popsetIds=[]) {
    this.updateLoadingStatus({
      loading: true,
      loadingWarning: null,
    });

    let {rawDataCollection} = this.props;

    rawDataCollection.sequenceType = 'complete';
    rawDataCollection.loadFrom = 'Genbank';

    const {references} = rawDataCollection;

    const [{
      id: existingRefId,
      refName
    }] = references.concat(NOREFSUBST);

    // const knownGenbankIds = new Set(
    //   rawSequences.map(({accession}) => accession)
    // );

    // genbankIds = genbankIds.filter(
    //   accession => !knownGenbankIds.has(accession)
    // );

    let genbankIdsWithRefs = [[existingRefId, refName, genbankIds]];
    rawDataCollection = await this.loadGenbankSequences(
      genbankIdsWithRefs, rawDataCollection, false, false);

    genbankIdsWithRefs = [[existingRefId, refName, popsetIds]];
    if (popsetIds.length > 0) {
      rawDataCollection = await this.loadGenbankSequences(
        genbankIdsWithRefs, rawDataCollection, true, false);
    }

    rawDataCollection = await this.alignSequences(rawDataCollection);
    rawDataCollection = await loadFromLocalDB(rawDataCollection);

    this.updateLoadingStatus({
      loading: false,
    });

    const {onUpdate} = this.props;
    await onUpdate(rawDataCollection);
  }

  // async handleUploadMutationFile(files) {
  //   this.updateLoadingStatus({
  //     loading: true,
  //     loadingWarning: null,
  //   });

  //   let {rawDataCollection} = this.props;
  //   rawDataCollection.sequenceType = 'incomplete';

  //   let rawSequences = [];
  //   const {sequenceType, references: refs} = rawDataCollection;
  //   const [{refName, id: refId}] = refs.concat(NOREFSUBST);

  //   let {noGBIdx} = this.state;
  //   for (const file of files) {
  //     const sequences = await parseInputSequenceFile(
  //       file,
  //       noGBIdx,
  //       /* defaults = */
  //       {
  //         refName,
  //         existingRefId: refId || null
  //       },
  //       /* incomplete = */
  //       sequenceType === 'incomplete'
  //     );
  //     noGBIdx += sequences.length;
  //     rawSequences = rawSequences.concat(sequences);
  //   }

  //   let isolateNameToRecord = {};
  //   for (const seq of rawSequences) {
  //     const isolateName = seq.isolateName;
  //     const gene = seq.gene;
  //     const mutations = seq.mutations;
  //     let isolateRecord = isolateNameToRecord[isolateName] || {};
  //     if (Object.keys(isolateRecord).length === 0) {
  //       isolateRecord = seq;
  //     }
  //     let geneInfo = isolateRecord['mutationMap'] || {};
  //     geneInfo[gene] = mutations;
  //     isolateRecord['mutationMap'] = geneInfo;
  //     isolateNameToRecord[isolateName] = isolateRecord;
  //   }

  //   rawDataCollection.rawSequences = Object.values(isolateNameToRecord);
  //   this.setState({
  //     noGBIdx: noGBIdx,
  //   });

  //   const {onUpdate} = this.props;
  //   await onUpdate(rawDataCollection);
  // }

  async handleUploadMutationFile() {
    this.setState({
      showChooseSeqType: true,
    });
  }

  handleCloseChooseSeqType() {
    this.setState({
      showChooseSeqType: false,
    });
  }

  async handleChooseSeqtype(rawDataCollection) {
    const {onUpdate} = this.props;
    await onUpdate(rawDataCollection);
  }

  async handleLoadFromFastaFile(files) {
    this.updateLoadingStatus({
      loading: true,
      loadingWarning: null,
    });

    let {rawDataCollection} = this.props;
    rawDataCollection.sequenceType = 'complete';
    rawDataCollection.loadFrom = 'Fasta file';

    let rawSequences = [];
    const {sequenceType, references: refs} = rawDataCollection;
    const [{refName, id: refId}] = refs.concat(NOREFSUBST);

    let {noGBIdx} = this.state;
    for (const file of files) {
      const sequences = await parseInputSequenceFile(
        file,
        noGBIdx,
        /* defaults = */
        {
          refName,
          existingRefId: refId || null
        },
        /* incomplete = */
        sequenceType === 'incomplete'
      );
      noGBIdx += sequences.length;

      // sequenceType === 'complete'
      // use GenBank data to update sequences
      // If load from tsv/fasta file, don't have to load from genbank
      // const genbankSequenceMap = sequences
      //   .filter(seq => !seq.accession.startsWith('noGB'))
      //   .reduce((acc, seq) => {
      //     acc[seq.accession] = seq;
      //     return acc;
      //   }, {});
      // const genbankIdsWithRefs = [[
      //   refId || null, refName,
      //   Object.values(genbankSequenceMap).map(seq => seq.accession)]];
      // const rawSeqs = batchFetchGenbankSequences(genbankIdsWithRefs, false);
      // for await (const rawSeq of rawSeqs) {
      //   const origSeq = genbankSequenceMap[rawSeq.accession];
      //   Object.assign(origSeq, rawSeq, {...origSeq});
      // }


      rawSequences = rawSequences.concat(sequences);
    }
    rawDataCollection.rawSequences = rawSequences;
    this.setState({
      noGBIdx: noGBIdx,
    });

    rawDataCollection = await this.alignSequences(rawDataCollection);
    rawDataCollection = await loadFromLocalDB(rawDataCollection);

    if (rawDataCollection.rawSequences.length === 0) {
      const loadingWarning = 'No valid sequence found.';
      this.updateLoadingStatus({
        loadingWarning: loadingWarning,
      });
    }
    this.updateLoadingStatus({
      loading: false,
    });

    const {onUpdate} = this.props;
    await onUpdate(rawDataCollection);
  }

  get PMIDButton() {
    const {rawDataCollection} = this.props;
    const {
      sequenceType,
      reference
    } = rawDataCollection;
    const incomplete = sequenceType === 'incomplete';

    const loadFromHIVDB = reference.loadFrom === 'HIVDB';

    if (incomplete) {
      return null;
    }
    else if (rawDataCollection.hasRawSequences && !loadFromHIVDB) {
      return null;
    }
    else {
      return (
        <span className={style.genbankBtn}>
          <Button
           onClick={
            this.handleLoadFromPMID.bind(this)}>
            PMID
          </Button>
        </span>
      );
    }
  }

  get fastaButton() {
    const {rawDataCollection} = this.props;
    const {
      sequenceType,
      reference
    } = rawDataCollection;
    const incomplete = sequenceType === 'incomplete';

    const loadFromHIVDB = reference.loadFrom === 'HIVDB';

    const {fileName} = this.state;

    if (incomplete) {
      return null;
    }
    else if (rawDataCollection.hasRawSequences && !loadFromHIVDB) {
      return null;
    }
    else {
      return (
        <FileInput
         name="uploadIsolateList"
         accept={
           'text/plain,.txt,' +
           // 'text/tab-separated-values,text/csv,.csv,.tsv,' +
           'text/x-fasta,.fasta,.fas,.fna'}
         children={
           'Fasta file'}
         hideSelected={true}
         onChange={
           this.handleLoadFromFastaFile.bind(this)}
         {...{fileName}}
         multiple
        />
      );
    }
  }

  render() {

    const {rawDataCollection} = this.props;

    const {loadingStatus} = this.state;

    if (loadingStatus.loading) {
      return (
        <div>
          <Loading />
          <p>{loadingStatus.loadingText}</p>
        </div>
      );
    }

    const {showChooseSeqType} = this.state;
    const {
      sequenceType,
      references: refs,
    } = rawDataCollection;
    const incomplete = sequenceType === 'incomplete';
    const showAccessionBtn = refs.length <= 1 && !incomplete;

    if (incomplete) {
      return 'Please enter mutation list in table below.';
    }

    return (
      <div>
        {rawDataCollection.reference.loadFrom === 'HIVDB'?
          <div>
            Data has been loaded from HIVDB, please see table below.
          </div>: null
        }

        <div className={style['load-buttons']}>
          {rawDataCollection.reference.loadFrom === 'HIVDB'?
            <span>Add more sequence from:{' '}</span> :
            <span>Load Sequence from:{' '}</span>
          }

          <FormInline dom="span">
            {this.PMIDButton}

            {showAccessionBtn ?
              <GenbankButton
               onChange={
                this.handleLoadFromAccession.bind(this)} />
            : null}

            {this.fastaButton}

            <Button
             onClick={
              this.handleUploadMutationFile.bind(this)}
            >
              {!rawDataCollection.hasRawSequences ? '': 'Append '}Mutation list
            </Button>

            {showChooseSeqType?
              <ChooseSeqtypeDialog
               rawDataCollection={rawDataCollection}
               onUpdate={this.handleChooseSeqtype.bind(this)}
               onClose={this.handleCloseChooseSeqType.bind(this)}
              /> :
              null
            }

          </FormInline>
        </div>
        <div className={style.warning}>
          {loadingStatus.loadingWarning}
        </div>
        <hr/>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/views/wizard/isolate/upload/upload-panel.js